import { Button, Form, Input, message, Select } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import useBoolean from 'common/hooks/useBoolean';
import { validateEmail } from 'common/helpers/validate';
import { validateTelephone } from '~helpers/validate';
import { phoneNumberJoin, phoneNumberSplit } from '~helpers/order';
import countries from '~constants/countries';
import { MUTATION_UPDATE_LANDLORD_LINK_MAN, MUTATION_CREATE_LANDLORD_LINK_MAN } from '../queries';

const uesStyles = makeStyles(
  theme => ({
    wrap: {
      width: '50%',
      marginTop: 24,
      paddingBottom: 24,

      '& .ant-form-item-label': {
        ...theme.typography.body2,
        marginBottom: 6,
        lineHeight: '22px',
      },

      '& .ant-form-item': {
        marginBottom: 16,
      },
    },
    label: {
      ...theme.typography.h5,
      color: theme.colors.titleColor,
      margin: 0,
    },
    value: {
      ...theme.typography.body2,
      display: 'block',
      marginBottom: 6,
      color: theme.colors.bodyColor,
    },
    title: {
      ...theme.flex.mainSpaceBetween,
      marginBottom: 6,
    },
    editingTitle: {
      marginBottom: 16,
    },
    editBtn: {
      ...theme.typography.h5,
      height: 22,
      padding: 0,
      color: theme.colors.mainGreen6,
      border: '0 !important',
      boxShadow: 'none !important',
    },
    addBtn: {
      ...theme.typography.body2,
      height: 22,
      padding: 0,
      color: theme.colors.mainGreen6,
      border: '0 !important',
      boxShadow: 'none !important',
    },
    input: {
      height: 40,

      '& .ant-input': {
        height: 40,
      },
    },
  }),
  { name: 'FieldCard' },
);

const SelectPhoneNumberCode = ({ getFieldDecorator, phoneCode, ...otherProps }) => {
  const defaultPhoneCode = useMemo(() => {
    const countryCode = window.hurricane.clientInfo?.country?.toLowerCase();

    return countries.list.find(item => item.iso2Code === countryCode)?.phoneCode || '+86';
  }, []);

  return getFieldDecorator('phoneCode', {
    initialValue: phoneCode || defaultPhoneCode,
  })(
    <Select style={{ width: 76 }} {...otherProps}>
      <For each="item" of={countries.list} index="index">
        <Select.Option key={index} value={item.phoneCode}>
          {item.phoneCode}
        </Select.Option>
      </For>
    </Select>,
  );
};

SelectPhoneNumberCode.propTypes = {
  getFieldDecorator: PropTypes.func.isRequired,
};

const ContactInformationCard = ({ landlordContact, refetchQueryLandlord, form }) => {
  const classes = uesStyles();
  const { t } = useTranslation();

  const { landlordId, enabled, landlordLinkMen, firstName, lastName } = landlordContact;
  const {
    contactEmail: email,
    contactPhone: phone,
    id: landlordLinkMenId,
  } = landlordLinkMen?.[0] ?? {};
  const { getFieldDecorator, validateFields } = form;

  const [edit, { toggle: setEdit }] = useBoolean(false);
  const [showAddNewBtn, { toggle: setShowAddNewBtn }] = useBoolean(true);

  const [mutationUpdateLandlordContact, { loading: updateLandlordLinkManLoading }] = useMutation(
    MUTATION_UPDATE_LANDLORD_LINK_MAN,
    {
      onCompleted: setEdit,
    },
  );

  const [mutationCreateLandlordLinkMan, { loading: createLandlordLinkManLoading }] = useMutation(
    MUTATION_CREATE_LANDLORD_LINK_MAN,
    {
      onCompleted: () => {
        setShowAddNewBtn();
        refetchQueryLandlord();
      },
      onError: e => {
        message.error(e?.toString());
      },
    },
  );

  const handleConfirm = () => {
    validateFields((err, values) => {
      if (!err) {
        const inputValues = {};

        if (values?.email) {
          inputValues.contactEmail = values?.email;
        }

        if (values?.phone) {
          inputValues.contactPhone = phoneNumberJoin({
            phoneCode: values?.phoneCode,
            phoneNumber: values?.phone,
          });
        }

        if (landlordLinkMenId) {
          inputValues.id = landlordLinkMenId;
          mutationUpdateLandlordContact({ variables: { input: inputValues } });
        } else {
          inputValues.landlordId = landlordId;
          inputValues.contactName = `${firstName} ${lastName}`;

          mutationCreateLandlordLinkMan({ variables: { input: inputValues } });
        }
      }
    });
  };

  const disabledEditBtn = useMemo(
    () => !enabled || updateLandlordLinkManLoading || createLandlordLinkManLoading,
    [enabled, updateLandlordLinkManLoading, createLandlordLinkManLoading],
  );

  const phoneNumber = useMemo(() => phoneNumberSplit(phone), [phone]);

  const handleClickAddNewBtn = () => {
    if (!phone && !email) {
      setEdit();
    }
    setShowAddNewBtn();
  };

  const handleCloseForm = () => {
    setEdit();
    setShowAddNewBtn();
  };

  return (
    <div className={classes.wrap}>
      <div className={classNames(classes.title, { [classes.editingTitle]: edit })}>
        <h5 className={classes.label}>{t('contact_information')}</h5>
        <If condition={phone || email}>
          <Button ghost className={classes.editBtn} onClick={setEdit} disabled={disabledEditBtn}>
            {edit ? t('cancel') : t('edit')}
          </Button>
        </If>
        <If condition={!phone && !email && edit}>
          <Button
            ghost
            className={classes.editBtn}
            onClick={handleCloseForm}
            disabled={disabledEditBtn}
          >
            {t('cancel')}
          </Button>
        </If>
      </div>
      <div>
        <If condition={phone && !edit}>
          <span className={classes.value}>{phone}</span>
        </If>
        <If condition={email && !edit}>
          <span className={classes.value}>{email}</span>
        </If>

        <If condition={!showAddNewBtn || edit}>
          <Form hideRequiredMark>
            <If condition={(!showAddNewBtn && !phone) || (edit && phone)}>
              <Form.Item label={!phone && !email ? t('phone') : ''} colon={false}>
                {getFieldDecorator('phone', {
                  initialValue: phoneNumber?.phoneNumber,
                  validateTrigger: 'onBlur',
                  rules: [
                    {
                      required: true,
                      message: t('this_part_cannot_be_empty'),
                    },
                    {
                      message: '',
                      validator: (rule, value, callback) => {
                        if (value === '') {
                          callback();
                          return;
                        }
                        if (!validateTelephone(value)) {
                          callback(t('please_input_valid_phone_number_address'));
                        }
                        callback();
                      },
                    },
                  ],
                })(
                  <Input
                    className={classes.input}
                    addonBefore={
                      <SelectPhoneNumberCode
                        getFieldDecorator={getFieldDecorator}
                        phoneCode={phoneNumber?.phoneCode}
                      />
                    }
                    placeholder={t('please_enter_a_phone_number_address')}
                  />,
                )}
              </Form.Item>
            </If>

            <If condition={(!showAddNewBtn && !email) || (edit && email)}>
              <Form.Item
                label={!phone && !email ? t('email') : ''}
                colon={false}
                className={classes.emailLabel}
              >
                {getFieldDecorator('email', {
                  initialValue: email,
                  validateTrigger: 'onBlur',
                  rules: [
                    {
                      required: true,
                      message: t('this_part_cannot_be_empty'),
                    },
                    {
                      message: '',
                      validator: (rule, value, callback) => {
                        if (value === '') {
                          callback();
                          return;
                        }
                        if (!validateEmail(value)) {
                          callback(t('please_input_valid_email_address'));
                        }
                        callback();
                      },
                    },
                  ],
                })(
                  <Input
                    placeholder={t('please_enter_a_email_address')}
                    className={classes.input}
                  />,
                )}
              </Form.Item>
            </If>
          </Form>
          <Button
            type="primary"
            style={{ height: 40, fontWeight: 'bold' }}
            onClick={handleConfirm}
            loading={updateLandlordLinkManLoading || createLandlordLinkManLoading}
          >
            {t('confirm')}
          </Button>
        </If>

        <If condition={(!phone || !email) && showAddNewBtn}>
          <Button
            ghost
            className={classes.addBtn}
            onClick={handleClickAddNewBtn}
            disabled={disabledEditBtn}
          >
            <Choose>
              <When condition={!phone && !email}>{t('add_phone_or_email_contact')}</When>
              <When condition={!phone}>{t('add_phone_contact')}</When>
              <When condition={!email}>{t('add_email_contact')}</When>
            </Choose>
          </Button>
        </If>
      </div>
    </div>
  );
};

ContactInformationCard.propTypes = {
  landlordContact: PropTypes.object.isRequired,
  form: PropTypes.object,
  refetchQueryLandlord: PropTypes.func,
};

export default Form.create({
  name: 'ContactInformationCard',
})(ContactInformationCard);
